import { Multiplayer } from "../../../shared/constants";
import { Debug } from "../../../shared/debug";
import { TrainingLevel } from "../../../shared/playerAccount";
import { getVersionNumber } from "../../../shared/version";
import { HostTransport, makeReplayGameStats } from "../../../shared/replayStats";
import { GameClient } from "../gameClient";
import { rememberUploadedReplay, getLadderRank, getUploadedReplays } from "../userSettings";
import { getGameTotals, getPlayedToday } from "../userStats";
import { getWebServicesList } from "./webServicesList";
import backupReplay from "../../public/replays/backup_replay.json";

export type UploaderStats = { isOwnReplay: boolean, opponentRank: number | null, playTimeMs: number | null, hostTransport: HostTransport | null };

export function reportReplay(saveData: string, uploaderStats: UploaderStats) {
	try {
		console.log('Reporting replay to server...');
		if (Debug.DontReportReplay) return; //Don't send replays to server if we are playing against the last replay

		// //Send this to the matchmaking server to save with other players
		const rank = getLadderRank();
		const data = { clientId: 'dev-client', gameData: withUploadStats(saveData, rank, uploaderStats), version: getVersionNumber(), rank };
		uploadReplay(JSON.stringify(data), rank).then(() => {
			console.log('Replay uploaded successfully');
		}).catch((error) => {
			console.error('Failed to upload replay:', error);
		});
	}
	catch (error) {
		console.error('Failed to report replay', error); //Only stats, never worth stopping a game over
	}
}

export function reportReplacedSaveReplays(oldSaveData: string | null, newGameId: string | undefined) {
	if (!oldSaveData) return;
	try {
		const oldSave = JSON.parse(oldSaveData);
		//Saves from before games had ids can't be told apart from the next save of the same game, so they're skipped
		if (!oldSave.gameId || oldSave.gameId === newGameId) return;
		const bot = (oldSave.players ?? []).find((player: { isBot: boolean }) => player.isBot);
		for (const player of oldSave.players ?? []) {
			if (player.isBot) continue;
			const replay = oldSave.replaysByPlayer?.[player.id];
			if (!replay) continue;
			replay.isInvalid = oldSave.endlessMode ? 'endless' : 'abandoned';
			const opponentReplayID = bot ? oldSave.replaysByPlayer?.[bot.id]?.replayID : undefined;
			replay.stats = makeReplayGameStats(oldSave.trainingLevel, 'abandoned', oldSave.roundNumber, oldSave.players ?? [], oldSave.gameId, opponentReplayID);
			reportReplay(JSON.stringify(replay), {
				isOwnReplay: player.id === oldSave.hostPlayerId,
				opponentRank: oldSave.opponentRank ?? null,
				playTimeMs: oldSave.playTimeMs ?? null,
				hostTransport: oldSave.hostTransport ?? null,
			});
		}
	}
	catch (error) {
		console.error('Could not report the replays of a replaced save', error); //Only stats
	}
}

function withUploadStats(replayData: string, rank: number, uploaderStats: UploaderStats) {
	try {
		const replay = JSON.parse(replayData);
		if (!replay.stats) return replayData;
		if (uploaderStats.hostTransport) replay.stats.hostTransport = uploaderStats.hostTransport;
		if (uploaderStats.isOwnReplay) {
			replay.stats.rank = rank;
			replay.stats.totals = getGameTotals();
			replay.stats.playedToday = getPlayedToday();
			if (replay.stats.mode === 'ranked' && uploaderStats.opponentRank !== null) replay.stats.opponentRank = uploaderStats.opponentRank;
			if (uploaderStats.playTimeMs !== null) replay.stats.playTimeSeconds = Math.round(uploaderStats.playTimeMs / 1000);
		}
		return JSON.stringify(replay);
	}
	catch (error) {
		console.error('Could not add the upload stats to the replay', error); //Only stats, upload it as is
		return replayData;
	}
}

export async function chooseOpponent(gameClient: GameClient) {
	let replayData: string | undefined;
	let replayName: string;
	let opponentRank: number | null = null;
	let replayID: string | undefined;

	if (Debug.PlayAgainstLastReplay) {
		replayName = "last_replay";
		replayID = replayName;
		replayData = localStorage.getItem(replayName) || undefined;
	}
	else if (gameClient.trainingLevel === TrainingLevel.Trained) {
		//Query replay from server
		replayName = "ranked_opponent";
		const rank = getLadderRank();
		try {
			const downloaded = await downloadReplay(rank);
			replayData = downloaded?.data;
			opponentRank = downloaded?.rank ?? null;
			replayID = downloaded?.replayID;
		}
		catch (e) {
			console.log(`Failed to get replay data from server, using offline backup`);
		}
		if (!replayData) {
			console.log('no replay data from server, using offline backup');
			replayData = JSON.stringify(backupReplay)
			replayID = "backup_replay";
		}
	}
	else {
		replayName = "tutorial_replay";
		replayID = replayName;
		replayData = localStorage.getItem(replayName) || undefined;
	}

	return { name: replayName, data: replayData, replayID, opponentRank };
}

export function hasTutorialReplay() {
	try {
		const data = localStorage.getItem("tutorial_replay");
		if (!data) return false;
		const replay = JSON.parse(data);
		return Array.isArray(replay.commandList) && Array.isArray(replay.shopFreezesByRound);
	}
	catch (e) {
		return false;
	}
}

async function downloadReplay(rank: number) {
	const Services = await getWebServicesList();
	const indexTemplate = Debug.UseDevReplays && Services.DevReplaysIndexURLTemplate ? Services.DevReplaysIndexURLTemplate : Services.PublicReplaysIndexURLTemplate;
	const opponentIndex = indexTemplate.replace('[VERSION]', getVersionNumber());
	const response = await fetch(opponentIndex, { method: 'GET' });
	const index = await response.json();
	const replays = index.replays;
	const ranks = Object.keys(replays);

	//Leave out our own uploads
	const ownReplays = new Set(getUploadedReplays());
	const isOwnReplay = (url: string) => ownReplays.has(replayFileName(url));

	//Find nearest rank with replays, keeping our own as a fallback for when there is nothing else
	const isCloser = (r: number, best: number) => Math.abs(r - rank) < Math.abs(best - rank);
	let nearestRankWithReplays = Infinity;
	let candidates: Array<{ url: string }> = [];
	let nearestRankWithOwnReplays = Infinity;
	let ownCandidates: Array<{ url: string }> = [];

	for (const r of ranks) {
		const entries: Array<{ url: string }> = replays[r];
		const others = entries.filter(entry => !isOwnReplay(entry.url));
		const own = entries.filter(entry => isOwnReplay(entry.url));
		if (others.length > 0 && isCloser(Number(r), nearestRankWithReplays)) {
			nearestRankWithReplays = Number(r);
			candidates = others;
		}
		if (own.length > 0 && isCloser(Number(r), nearestRankWithOwnReplays)) {
			nearestRankWithOwnReplays = Number(r);
			ownCandidates = own;
		}
	}

	if (candidates.length === 0 && ownCandidates.length > 0) {
		console.log('Only our own replays are available, playing against one of those');
		nearestRankWithReplays = nearestRankWithOwnReplays;
		candidates = ownCandidates;
	}

	if (nearestRankWithReplays === Infinity) {
		console.log('no suitable rank found');
		return null;
	} else {
		console.log(`Found nearest rank with replays: ${nearestRankWithReplays}`);
		const randomReplayAtRank = Math.floor(Math.random() * candidates.length);
		const replayURI = candidates[randomReplayAtRank].url;
		const response = await fetch(replayURI, { method: 'GET' });
		const replayData = await response.json();
		console.log(`GOT REPLAY DATA`);
		const replayStr: string = replayData.gameData;
		return { data: replayStr, rank: nearestRankWithReplays, replayID: replayFileName(replayURI).replace(/\.json$/, '') };
	}
}

function getByteLengthUTF8(str: string) {
	const encoder = new TextEncoder();
	const byteArray = encoder.encode(str);
	return byteArray.length;
}


async function getUploadUrl(rank: number, size: number) {
	const postData = JSON.stringify({
		rank: rank.toString(),
		contentType: 'application/json',
		// The presigned URL is pinned to this length, so it has to be the exact
		// byte count of the body we are about to PUT - not the string length.
		size
	});
	const Services = await getWebServicesList();
	const uploadUrl = Debug.UseDevReplays && Services.DevUploadReplayURL ? Services.DevUploadReplayURL : Services.UploadReplayURL;
	const response = await fetch(uploadUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			"Content-Length": getByteLengthUTF8(postData).toString()
		},
		body: postData,
	});
	const json = await response.json();

	if (json.uploadUrl && json.key) {
		return { uploadUrl: json.uploadUrl as string, key: json.key as string };
	}
	throw new Error('Failed to get upload URL');
}

async function uploadReplay(replay: string, rank: number) {
	const { uploadUrl, key } = await getUploadUrl(rank, getByteLengthUTF8(replay));
	const response = await fetch(uploadUrl, {
		method: 'PUT',
		body: replay,
		headers: {
			'Content-Type': 'application/json'
		}
	});
	if (!response.ok) {
		throw new Error('Failed to upload replay');
	}
	rememberUploadedReplay(replayFileName(key));
}

/** The uuid.json part, which stays the same from upload key ("rank/uuid.json") to published url (".../version/rank/uuid.json") */
function replayFileName(keyOrUrl: string) {
	return keyOrUrl.split('/').pop() ?? keyOrUrl;
}